/** A wrapper for fetch that expects a JSON and throws a typed error if fails */
async function fetchJson({ url, options, }) {
    const { method, headers = {}, body, credentials = "include" } = options;
    const finalHeaders = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "Content-Type": "application/json",
        ...headers,
    };
    const response = await fetch(url, {
        method,
        headers: finalHeaders,
        body: body ? JSON.stringify(body) : undefined,
        credentials,
    });
    if (!response.ok) {
        const contentType = response.headers.get("content-type") || "";
        let errorBody;
        try {
            errorBody = contentType.includes("application/json")
                ? await response.json()
                : await response.text();
        }
        catch {
            errorBody = null;
        }
        const error = {
            name: "FetchingError",
            message: `Request failed with status ${response.status}`,
            status: response.status,
            errorBody,
        };
        throw error;
    }
    return response.json();
}

/**
 * Reusable abstraction wrapping the logic to communicating with core api
 * Allows to make type-safe GET and POST requests
 */
class BaseApiClient {
    baseUrl;
    headers;
    constructor(baseUrl, headers = {}) {
        this.baseUrl = baseUrl;
        this.headers = headers;
    }
    /** Sends a GET request and return the data property */
    async get(path) {
        try {
            const response = await fetchJson({
                url: `${this.baseUrl}${path}`,
                options: {
                    method: "GET",
                    headers: this.headers,
                },
            });
            return response.data;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /** Sends a POST request and return the data property */
    async post(path, body) {
        try {
            const response = await fetchJson({
                url: `${this.baseUrl}${path}`,
                options: {
                    method: "POST",
                    headers: this.headers,
                    body,
                },
            });
            return response.data;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Check the error type
     * Throws an error with HTTP status and body if it is in type of fetchError
     * otherwise a generic error
     */
    handleError(error) {
        if (this.isFetchError(error)) {
            throw new Error(`Fetch failed: ${error.status} ${error.errorBody}`);
        }
        else {
            throw new Error(`Unknown error: ${error}`);
        }
    }
    isFetchError(error) {
        return (typeof error === "object" &&
            error !== null &&
            "name" in error &&
            error.name === "FetchError" &&
            "status" in error &&
            "errorBody" in error);
    }
}

const userSettingsUrl = "/v3/users/me/settings";
/** All the APIs for fetching and updating user settings */
class UserSettingsApi extends BaseApiClient {
    /** Fetch the USER settings by providing an identifier */
    fetchUserSettings(identifier) {
        return this.get(`${userSettingsUrl}/${identifier}`);
    }
    /** Update the USER settings */
    updateUserSettings(identifier, payload) {
        return this.post(`${userSettingsUrl}/${identifier}`, payload);
    }
}

/** Returns the project settings url based on the projectId */
function projectSettingsUrl(projectId) {
    return `/v3/projects/${projectId}/settings`;
}
/** All the APIs for fetching and updating project settings */
class ProjectSettingsApi extends BaseApiClient {
    /** Fetch the PROJECT settings by providing an identifier */
    fetchProjectSettings(projectId, identifier) {
        return this.get(`${projectSettingsUrl(projectId)}/${identifier}`);
    }
    /** Update the PROJECT settings */
    updateProjectSettings(projectId, identifier, payload) {
        return this.post(`${projectSettingsUrl(projectId)}/${identifier}`, payload);
    }
}

/** Returns the company settings url based on the companyId */
function companySettingsUrl(companyId) {
    return `/v3/company/${companyId}/me/settings`;
}
/** All the APIs for fetching and updating company settings */
class CompanySettingsApi extends BaseApiClient {
    /** Fetch the COMPANY settings by providing an identifier */
    fetchCompanySettings(companyId, identifier) {
        return this.get(`${companySettingsUrl(companyId)}/${identifier}`);
    }
    /** Update the COMPANY settings */
    updateCompanySettings(companyId, identifier, payload) {
        return this.post(`${companySettingsUrl(companyId)}/${identifier}`, payload);
    }
}

/** Returns the device settings url based on the companyId and deviceId */
function deviceSettingsUrl(companyId, deviceId) {
    return `/v3/company/${companyId}/device/${deviceId}/settings`;
}
/** All the APIs for fetching and updating device settings */
class DeviceSettingsApi extends BaseApiClient {
    /** Fetch the DEVICE settings by providing an identifier */
    fetchDeviceSettings(companyId, deviceId, identifier) {
        return this.get(`${deviceSettingsUrl(companyId, deviceId)}/${identifier}`);
    }
    /** Update the DEVICE settings */
    updateDeviceSettings(companyId, deviceId, identifier, payload) {
        return this.post(`${deviceSettingsUrl(companyId, deviceId)}/${identifier}`, payload);
    }
}

/** Client for interacting with the settings API. */
class SettingsApiClient {
    user;
    project;
    company;
    device;
    constructor(baseUrl, headers = {}) {
        this.user = new UserSettingsApi(baseUrl, headers);
        this.project = new ProjectSettingsApi(baseUrl, headers);
        this.company = new CompanySettingsApi(baseUrl, headers);
        this.device = new DeviceSettingsApi(baseUrl, headers);
    }
}

var index$1 = /*#__PURE__*/Object.freeze({
    __proto__: null
});

// TODO: Update the logic after https://faro01.atlassian.net/browse/HBCORE-1702 is being implemented
/** Returns true if the settings returned from SettingsApi is the default one and user has not changed it yet */
function isDefaultSettings(modifiedBy) {
    return modifiedBy === "00000000-0000-0000-0000-000000000000";
}

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    isDefaultSettings: isDefaultSettings
});

export { SettingsApiClient, index$1 as SettingsApiTypes, index as SettingsApiUtils };
